'use client';

/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  AnimatedDiv,
  checkIsMobile,
  cn,
  Div,
  Image,
  ImageProps,
  KeyboardAvoidingView,
  useSafeAreaInsets,
} from '@scatterlab/zeta-core';
import {
  FC,
  memo,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { ModalHandleIcon } from './icons';

const getAutoHeight = (element: HTMLDivElement) => {
  const originalStyle = element.style.height;
  // eslint-disable-next-line no-param-reassign
  element.style.height = 'auto';
  const autoHeight = element.clientHeight;
  // eslint-disable-next-line no-param-reassign
  element.style.height = originalStyle;
  return autoHeight;
};

const INSET_ZERO = { top: 0, bottom: 0, left: 0, right: 0 } as const;

export type BottomSheetProps = {
  extensible?: boolean;
  onPressBackdrop?: () => void;
  containerClassName?: string;
  backdropVariant: 'blur' | 'opacity' | 'none';
  backgroundImage?: ImageProps['src'];
  topFixedContents?: ReactNode;
  children?: ReactNode;
  bottomFixedContents?: ReactNode;
  maxHeight?: number;
  className?: string;
  contentContainerClassName?: string;
  dragDisabled?: boolean;
  scrollViewDisabled?: boolean;
  modalHandleTranslucent?: boolean;
  modalHandleClassName?: string;
  dragArea?: 'full' | 'handle';
  handleHidden?: boolean;
  onExtendedChange?: (extended: boolean) => void;
};

const BottomSheet: FC<BottomSheetProps> = ({
  extensible,
  onPressBackdrop,
  containerClassName,
  backdropVariant,
  backgroundImage,
  topFixedContents,
  children,
  bottomFixedContents,
  maxHeight,
  className,
  contentContainerClassName,
  dragDisabled,
  scrollViewDisabled,
  modalHandleTranslucent,
  modalHandleClassName,
  dragArea,
  handleHidden,
  onExtendedChange,
}) => {
  const { top, bottom } = useSafeAreaInsets();

  const extensibleDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (extensibleDivRef.current) {
      extensibleDivRef.current.style.height = 'auto';
    }
  }, []);

  const possibleVisibleHeight = window.innerHeight;

  const effectiveMaxHeight = Math.min(
    maxHeight !== undefined ? maxHeight + bottom : possibleVisibleHeight,
    possibleVisibleHeight,
  );

  const handlePointerDown = useCallback(
    (e: ReactMouseEvent) => {
      if (dragDisabled) return;
      e.preventDefault();
      if (
        !extensibleDivRef.current ||
        effectiveMaxHeight === undefined ||
        (!extensible && !onPressBackdrop)
      ) {
        return;
      }
      const autoHeight = Math.min(
        getAutoHeight(extensibleDivRef.current),
        effectiveMaxHeight,
      );
      const isFull = extensibleDivRef.current.style.height !== '100vh';

      extensibleDivRef.current.style.transition = 'none';
      const firstY = e.clientY;
      const firstHeight = extensibleDivRef.current.clientHeight;
      const handleDrag = (ev: MouseEvent) => {
        if (!extensibleDivRef.current) return;
        extensibleDivRef.current.style.maxHeight = '';
        if (extensible) {
          extensibleDivRef.current.style.height = `${firstHeight + firstY - ev.clientY}px`;
        } else {
          extensibleDivRef.current.style.height = `${Math.min(
            firstHeight + firstY - ev.clientY,
            firstHeight,
          )}px`;
        }
      };

      const handleTouchMove = (ev: TouchEvent) => {
        if (!extensibleDivRef.current) return;
        extensibleDivRef.current.style.maxHeight = '';
        if (extensible) {
          extensibleDivRef.current.style.height = `${firstHeight + firstY - ev.touches[0]!.clientY}px`;
        } else {
          extensibleDivRef.current.style.height = `${Math.min(
            firstHeight + firstY - ev.touches[0]!.clientY,
            firstHeight,
          )}px`;
        }
      };

      const handleDragEnd = () => {
        if (checkIsMobile()) {
          window.removeEventListener('touchmove', handleTouchMove);
          window.removeEventListener('touchend', handleDragEnd);
        } else {
          window.removeEventListener('pointermove', handleDrag);
          window.removeEventListener('pointerup', handleDragEnd);
        }
        if (!extensibleDivRef.current) return;

        const extendingThreshold = isFull
          ? (autoHeight + window.innerHeight * 2) / 3 // 2/3 지점
          : (autoHeight * 2 + window.innerHeight) / 3; // 1/3 지점
        const closingThreshold = isFull ? 0.6 : 0.3;

        if (extensibleDivRef.current.clientHeight > extendingThreshold) {
          extensibleDivRef.current.style.height = '100vh';
          onExtendedChange?.(true);
        } else if (
          extensibleDivRef.current.clientHeight <= extendingThreshold &&
          extensibleDivRef.current.clientHeight >
            effectiveMaxHeight * closingThreshold
        ) {
          extensibleDivRef.current.style.height = `${autoHeight}px`;
          onExtendedChange?.(false);
        } else {
          onPressBackdrop?.();
        }
        extensibleDivRef.current.style.transition = 'height 0.3s ease-in-out';
      };

      if (checkIsMobile()) {
        window.addEventListener('touchmove', handleTouchMove);
        window.addEventListener('touchend', handleDragEnd);
      } else {
        window.addEventListener('pointermove', handleDrag);
        window.addEventListener('pointerup', handleDragEnd);
      }
    },
    [
      dragDisabled,
      effectiveMaxHeight,
      extensible,
      onExtendedChange,
      onPressBackdrop,
    ],
  );

  return (
    <KeyboardAvoidingView
      behavior="padding"
      className="absolute z-50 flex flex-1 flex-col items-center justify-center"
      style={{ ...INSET_ZERO, paddingTop: top }}
    >
      <AnimatedDiv
        role="presentation"
        className={cn('absolute inset-0 -z-20', {
          'backdrop-blur-[8px] bg-black/80': backdropVariant === 'blur',
          'bg-black/60': backdropVariant === 'opacity',
          'bg-transparent': backdropVariant === 'none',
        })}
        style={INSET_ZERO}
        onPress={onPressBackdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      <AnimatedDiv
        className={cn(
          'relative z-0 mt-auto flex w-full max-w-full flex-col rounded-b-none rounded-t-[20px] bg-gray-sub1 px-4 shadow-400',
          containerClassName,
        )}
        ref={extensibleDivRef}
        style={{
          paddingBottom: bottom + 16,
          maxHeight: effectiveMaxHeight,
        }}
        initial={{ translateY: 200 }}
        animate={{ translateY: 0 }}
        exit={{ translateY: 200 }}
        transition={{
          duration: 0.3,
          dampingRatio: 1,
          stiffness: 100,
        }}
      >
        {backgroundImage && (
          <AnimatedDiv
            className="absolute inset-0 -z-10 h-full w-screen rounded-t-[20px] object-cover"
            style={INSET_ZERO}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Image
              src={backgroundImage}
              alt=""
              fill
              className="h-full w-screen rounded-t-xl object-cover"
            />
          </AnimatedDiv>
        )}
        {!handleHidden && (
          <Div
            className={cn(
              modalHandleTranslucent ? 'absolute z-10 w-full' : 'relative',
              'flex h-[35px] shrink-0 select-none flex-row items-center justify-center gap-2',
              modalHandleClassName,
            )}
            onPointerDown={handlePointerDown}
          >
            <ModalHandleIcon
              className={cn(
                'h-[3px] w-[29px]',
                modalHandleTranslucent ? 'text-white/50' : 'text-gray-700',
              )}
            />
          </Div>
        )}
        {topFixedContents}
        <Div
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-y-auto',
            className ?? contentContainerClassName,
          )}
        >
          {children}
        </Div>
        {bottomFixedContents}
      </AnimatedDiv>
    </KeyboardAvoidingView>
  );
};

export default memo(BottomSheet);
